import { useEffect, useRef } from "react";
import Bubble, { type BubbleData } from "./Bubble";
import "./BubbleRender.scss";
import { useSession } from "@/Recoil/Session/Session";
import { FastAvatar } from "@/Components/FastAvatar";

interface BubbleRenderProps {
    item: BubbleData & {
        id: string;
        senderid?: string;
        senderName?: string;
        senderAvatar?: string;
    };
    onLongPress?: (messageid: string) => void;
    onProfileClick?: (senderid: string) => void;
}

export default function BubbleRender({ item, onLongPress, onProfileClick }: BubbleRenderProps) {
    const { session } = useSession();
    const containerRef = useRef<HTMLDivElement>(null);

    // Fade-in animation
    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.style.opacity = "1";
        }
    }, []);

    const isOwnMessage = item.senderid === session?.addressid || item.senderid === session?.userid || item.role === "user";
    const showAvatar = !isOwnMessage && item.showSenderInfo;

    const handleProfileClick = () => {
        if (onProfileClick && item.senderid) {
            onProfileClick(item.senderid);
        }
    };

    // Reading message (streaming)
    if (item.id.startsWith("reading-")) {
        return (
            <div className="BubbleRender Left" ref={containerRef}>
                <div className="AvatarSpace">
                    {showAvatar && (
                        <button type="button" className="AvatarButton" onClick={handleProfileClick}>
                            <FastAvatar uri={item.senderAvatar} userid={item.senderid || 'bot'} size={32} style={{ borderRadius: '50%' }} />
                        </button>
                    )}
                </div>
                <div className="BubbleContent">
                    {showAvatar && (
                        <button type="button" className="SenderName" onClick={handleProfileClick}>
                            {item.senderName ?? "??"}
                        </button>
                    )}
                    <Bubble
                        key={item.id}
                        bubbleData={item}
                        position="left"
                        onLongPress={onLongPress}
                    />
                </div>
            </div>
        );
    }

    // System full-width image
    if (item.senderid === "system" && item.type === "images" && item.images?.length === 1) {
        return (
            <div className="BubbleRender SystemImage" ref={containerRef}>
                <a
                    href={item.images[0]}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <img src={item.images[0]} alt="" />
                </a>
            </div>
        );
    }

    // Regular message
    return (
        <div className={`BubbleRender ${isOwnMessage ? "Right" : "Left"}`} ref={containerRef}>
            {!isOwnMessage && (
                <div className="AvatarSpace">
                    {showAvatar && (
                        <button type="button" className="AvatarButton" onClick={handleProfileClick}>
                            <FastAvatar uri={item.senderAvatar} userid={item.senderid || 'bot'} size={32} style={{ borderRadius: '50%' }} />
                        </button>
                    )}
                </div>
            )}
            <div className="BubbleContent">
                {showAvatar && (
                    <button type="button" className="SenderName" onClick={handleProfileClick}>
                        {item.senderName ?? "??"}
                    </button>
                )}
                <Bubble
                    key={item.id}
                    bubbleData={item}
                    position={isOwnMessage ? "right" : "left"}
                    onLongPress={onLongPress}
                />
            </div>
        </div>
    );
}
